import type { AgentType, AgentDebugData } from '../types/debug';

/** Describes a grounding data section to render in the grounding tab */
export interface GroundingSection {
  /** Unique key for React list rendering */
  key: string;
  /** Display label shown in the card header */
  label: string;
  /** Which field on AgentDebugData holds the raw string data */
  dataField: 'conversationHistory' | 'transcript';
  /** Which rendering strategy to use */
  renderer: 'conversation' | 'transcript';
  /** Whether the card starts expanded */
  defaultExpanded: boolean;
}

interface GroundingConfig {
  /** Label shown on the top-level tab */
  tabLabel: string;
  /** Grounding sections rendered inside the tab */
  sections: GroundingSection[];
}

const GROUNDING_CONFIG: Record<AgentType, GroundingConfig> = {
  PA: {
    tabLabel: 'Conversations',
    sections: [
      {
        key: 'conversationHistory',
        label: 'Channel Conversations',
        dataField: 'conversationHistory',
        renderer: 'conversation',
        defaultExpanded: true,
      },
    ],
  },
  MA: {
    tabLabel: 'Transcript',
    sections: [
      {
        key: 'transcript',
        label: 'Meeting Transcript',
        dataField: 'transcript',
        renderer: 'transcript',
        defaultExpanded: true,
      },
    ],
  },
  Unknown: {
    tabLabel: 'Grounding Data',
    sections: [
      {
        key: 'conversationHistory',
        label: 'Conversation History',
        dataField: 'conversationHistory',
        renderer: 'conversation',
        defaultExpanded: true,
      },
      {
        key: 'transcript',
        label: 'Transcript',
        dataField: 'transcript',
        renderer: 'transcript',
        defaultExpanded: true,
      },
    ],
  },
};

/** Get the tab label for the grounding tab based on agent type */
export function getGroundingTabLabel(agentType: AgentType): string {
  return (GROUNDING_CONFIG[agentType] ?? GROUNDING_CONFIG.Unknown).tabLabel;
}

/** Get grounding sections for a given agent type, filtered to those with data */
export function getGroundingSections(
  agentType: AgentType,
  data: AgentDebugData,
): GroundingSection[] {
  const config = GROUNDING_CONFIG[agentType] ?? GROUNDING_CONFIG.Unknown;
  return config.sections.filter((s) => !!data[s.dataField]);
}
